import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowUp, ChevronsUpDown, Download, ExternalLink, MousePointerClick, Package, Percent } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useAnalyticsApi } from '../../../common/hooks/use-analytics-api.hook';
import { AnalyticsPeriod, BarList, MiniStat, Thumb } from '../../../components/design-system/analytics';
import { KpiCard, SearchField, SectionCard, TablePagination, formatNumber } from '../../../components/design-system/admin';
import { NoTrafficState } from '../../../modules/vendor/analytics/viz/components/NoTrafficState';
import { ReadingCard } from '../../../modules/vendor/analytics/viz/components/ReadingCard';
import { breakdownMap, deltaOf, valueOf } from '../../../modules/vendor/analytics/viz/metrics';
import {
  PRODUCT_CSV_COLUMNS,
  ProductRow,
  ProductSort,
  neverTried,
  productMovers,
  productReading,
  productTotals,
  productsCsv,
  sortProducts,
  toProductRows,
  triedNotSaved,
} from '../../../modules/vendor/analytics/viz/product-performance';
import { useLoader } from '../../../modules/vendor/analytics/viz/use-loader';
import { VIZ_LINKS } from '../../../modules/vendor/analytics/viz/links';
import { download } from './components/DashboardExportMenu';

const COLUMNS: { key: ProductSort; label: string }[] = [
  { key: 'tries', label: 'TRIES' },
  { key: 'downloads', label: 'SAVED' },
  { key: 'saveRate', label: 'SAVE_RATE' },
  { key: 'leads', label: 'LEADS' },
  { key: 'change', label: 'CHANGE' },
];

const pct = (rate: number | null) => (rate == null ? '—' : `${formatNumber(rate * 100, rate < 0.1 ? 1 : 0)}%`);

/**
 * Which products work in the visualizer: tried, kept, turned into leads —
 * and which ones fall, disappoint or are never tried at all.
 */
export const ProductPerformancePage: React.FC<{ period: AnalyticsPeriod }> = ({ period }) => {
  const { t } = useTranslation();
  const analytics = useAnalyticsApi();
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ by: ProductSort; dir: 'asc' | 'desc' }>({ by: 'tries', dir: 'desc' });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [selected, setSelected] = useState<ProductRow | null>(null);

  const { data, loading, error, reload } = useLoader(async () => {
    const { startDate, endDate } = period;
    const [products, leads, tries, downloads, catalog] = await Promise.all([
      analytics.getProductAnalytics({ dateRange: { startDate, endDate } }, 500),
      analytics.queryMetric({ metric: 'active_leads', startDate, endDate, groupBy: 'product', limit: 500 }),
      analytics.queryMetric({ metric: 'product_applications', startDate, endDate, compareToPreviousPeriod: true }),
      analytics.queryMetric({ metric: 'result_downloads', startDate, endDate, compareToPreviousPeriod: true }),
      analytics.getCatalogProducts(500),
    ]);
    return { products, leads, tries, downloads, catalog };
  }, [period.startDate, period.endDate]);

  const rows = useMemo(() => toProductRows(data?.products?.data?.products, breakdownMap(data?.leads)), [data]);
  const totals = useMemo(() => productTotals(rows), [rows]);
  const dead = useMemo(() => (data ? neverTried(data.catalog.products, rows) : []), [data, rows]);
  const deadCount = data ? (data.catalog.total > data.catalog.products.length ? Math.max(0, data.catalog.total - totals.triedProducts) : dead.length) : 0;
  const movers = useMemo(() => productMovers(rows), [rows]);
  const weak = useMemo(() => triedNotSaved(rows), [rows]);
  const reading = useMemo(
    () => productReading({ rows, catalogTotal: data?.catalog.total, neverTriedCount: deadCount }),
    [rows, data?.catalog.total, deadCount],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q ? rows.filter((r) => r.name.toLowerCase().includes(q) || (r.sku || '').toLowerCase().includes(q)) : rows;
    return sortProducts(list, sort.by, sort.dir);
  }, [rows, search, sort]);
  const pageRows = filtered.slice(page * pageSize, page * pageSize + pageSize);

  const exportCsv = () => {
    const csv = productsCsv(
      sortProducts(rows, 'tries'),
      PRODUCT_CSV_COLUMNS.map((c) => t(`VIZ_ANALYTICS.PRODUCTS.CSV.${c}`)),
    );
    download(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), `products_${period.startDate}_${period.endDate}.csv`);
  };

  const sortBy = (by: ProductSort) => {
    setSort((s) => (s.by === by ? { by, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { by, dir: 'desc' }));
    setPage(0);
  };

  if (!loading && (error || !data?.products)) {
    return (
      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">{t('VIZ_ANALYTICS.COMMON.LOAD_FAILED')}</p>
          <Button variant="outline" size="sm" onClick={reload}>
            {t('VIZ_ANALYTICS.COMMON.RETRY')}
          </Button>
        </CardContent>
      </Card>
    );
  }
  if (!loading && rows.length === 0) {
    return <NoTrafficState title={t('VIZ_ANALYTICS.PRODUCTS.EMPTY_TITLE')} description={t('VIZ_ANALYTICS.PRODUCTS.EMPTY_BODY')} />;
  }

  const vsPrevious = t('VIZ_ANALYTICS.COMMON.VS_PREVIOUS', { count: period.days });
  const saveRate = totals.tries > 0 ? totals.downloads / totals.tries : null;

  return (
    <div className="flex flex-col gap-6" data-testid="viz-products">
      <ReadingCard items={reading} loading={loading} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          testId="viz-products-kpi-tried"
          label={t('VIZ_ANALYTICS.PRODUCTS.KPI_TRIED')}
          value={formatNumber(totals.triedProducts)}
          hint={data?.catalog.total ? t('VIZ_ANALYTICS.PRODUCTS.OF_CATALOG', { count: data.catalog.total }) : undefined}
          icon={<Package />}
          loading={loading}
        />
        <KpiCard
          testId="viz-products-kpi-tries"
          label={t('VIZ_ANALYTICS.KPI.TRIES')}
          value={formatNumber(valueOf(data?.tries) ?? totals.tries)}
          delta={deltaOf(data?.tries)}
          hint={vsPrevious}
          icon={<MousePointerClick />}
          loading={loading}
        />
        <KpiCard
          testId="viz-products-kpi-downloads"
          label={t('VIZ_ANALYTICS.KPI.DOWNLOADS')}
          value={formatNumber(valueOf(data?.downloads) ?? totals.downloads)}
          delta={deltaOf(data?.downloads)}
          hint={vsPrevious}
          icon={<Download />}
          loading={loading}
        />
        <KpiCard
          testId="viz-products-kpi-rate"
          label={t('VIZ_ANALYTICS.PRODUCTS.KPI_SAVE_RATE')}
          value={pct(saveRate)}
          hint={t('VIZ_ANALYTICS.PRODUCTS.SAVE_RATE_HINT')}
          icon={<Percent />}
          loading={loading}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <SectionCard title={t('VIZ_ANALYTICS.PRODUCTS.RISING')} description={t('VIZ_ANALYTICS.PRODUCTS.RISING_DESCRIPTION')} className="min-w-0" testId="viz-products-rising">
          {loading ? (
            <ListSkeleton />
          ) : (
            <BarList
              emptyText={t('VIZ_ANALYTICS.PRODUCTS.NO_MOVERS')}
              items={movers.rising.map((m) => ({
                key: m.row.id,
                label: m.row.name,
                value: m.row.tries,
                leading: <Thumb src={m.row.thumbnailPath} alt={m.row.name} size="xs" />,
                secondary: <span className="text-success">+{formatNumber(m.delta)}</span>,
                onClick: () => setSelected(m.row),
              }))}
            />
          )}
        </SectionCard>
        <SectionCard title={t('VIZ_ANALYTICS.PRODUCTS.FALLING')} description={t('VIZ_ANALYTICS.PRODUCTS.FALLING_DESCRIPTION')} className="min-w-0" testId="viz-products-falling">
          {loading ? (
            <ListSkeleton />
          ) : (
            <BarList
              emptyText={t('VIZ_ANALYTICS.PRODUCTS.NO_MOVERS')}
              items={movers.falling.map((m) => ({
                key: m.row.id,
                label: m.row.name,
                value: m.row.tries,
                leading: <Thumb src={m.row.thumbnailPath} alt={m.row.name} size="xs" />,
                secondary: <span className="text-destructive">−{formatNumber(Math.abs(m.delta))}</span>,
                onClick: () => setSelected(m.row),
              }))}
            />
          )}
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <SectionCard title={t('VIZ_ANALYTICS.PRODUCTS.WEAK_TITLE')} description={t('VIZ_ANALYTICS.PRODUCTS.WEAK_DESCRIPTION')} className="min-w-0" testId="viz-products-weak">
          {loading ? (
            <ListSkeleton />
          ) : (
            <BarList
              emptyText={t('VIZ_ANALYTICS.PRODUCTS.WEAK_EMPTY')}
              items={weak.map((r) => ({
                key: r.id,
                label: r.name,
                value: r.tries,
                leading: <Thumb src={r.thumbnailPath} alt={r.name} size="xs" />,
                secondary: t('VIZ_ANALYTICS.PRODUCTS.SAVED_RATE', { count: r.downloads, rate: pct(r.saveRate) }),
                onClick: () => setSelected(r),
              }))}
            />
          )}
        </SectionCard>
        <SectionCard
          title={t('VIZ_ANALYTICS.PRODUCTS.NEVER_TITLE')}
          description={t('VIZ_ANALYTICS.PRODUCTS.NEVER_DESCRIPTION', { count: deadCount })}
          action={
            <Link to={VIZ_LINKS.catalog} className="text-xs text-muted-foreground hover:text-foreground">
              {t('VIZ_ANALYTICS.PRODUCTS.OPEN_CATALOG')}
            </Link>
          }
          className="min-w-0"
          testId="viz-products-never"
        >
          {loading ? (
            <ListSkeleton />
          ) : dead.length ? (
            <ul className="flex flex-col gap-1">
              {dead.slice(0, 6).map((p) => (
                <li key={p.groupId}>
                  <Link to={VIZ_LINKS.editProduct(p.uuid)} className="flex h-9 items-center gap-3 rounded-md px-2.5 text-sm hover:bg-accent/50">
                    <Thumb src={p.thumbnailPath} alt={p.name} size="xs" />
                    <span className="min-w-0 flex-1 truncate">{p.name}</span>
                    {p.sku && <span className="shrink-0 text-xs text-muted-foreground">{p.sku}</span>}
                  </Link>
                </li>
              ))}
              {deadCount > 6 && <li className="px-2.5 pt-1 text-xs text-muted-foreground">{t('VIZ_ANALYTICS.PRODUCTS.AND_MORE', { count: deadCount - 6 })}</li>}
            </ul>
          ) : (
            <p className="py-6 text-center text-sm text-muted-foreground">{t('VIZ_ANALYTICS.PRODUCTS.NEVER_EMPTY')}</p>
          )}
        </SectionCard>
      </div>

      <SectionCard
        title={t('VIZ_ANALYTICS.PRODUCTS.TABLE_TITLE')}
        description={t('VIZ_ANALYTICS.PRODUCTS.TABLE_DESCRIPTION')}
        action={
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={!rows.length} data-testid="viz-products-export">
            <Download />
            {t('VIZ_ANALYTICS.COMMON.EXPORT_CSV')}
          </Button>
        }
        flush
        testId="viz-products-table"
      >
        <div className="px-4 pb-3 sm:px-6">
          <SearchField
            value={search}
            onChange={(v) => {
              setSearch(v);
              setPage(0);
            }}
            placeholder={t('VIZ_ANALYTICS.PRODUCTS.SEARCH')}
          />
        </div>
        <div className="border-t">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[220px] ps-4 sm:ps-6">{t('VIZ_ANALYTICS.PRODUCTS.COL.PRODUCT')}</TableHead>
                {COLUMNS.map((c) => (
                  <TableHead key={c.key} className="text-end">
                    <button
                      type="button"
                      onClick={() => sortBy(c.key)}
                      className={cn('inline-flex items-center gap-1 whitespace-nowrap hover:text-foreground', sort.by === c.key && 'text-foreground')}
                    >
                      {t(`VIZ_ANALYTICS.PRODUCTS.COL.${c.label}`)}
                      {sort.by === c.key ? sort.dir === 'desc' ? <ArrowDown className="size-3" /> : <ArrowUp className="size-3" /> : <ChevronsUpDown className="size-3 opacity-50" />}
                    </button>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading
                ? [0, 1, 2, 3, 4].map((i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={COLUMNS.length + 1} className="ps-4 sm:ps-6">
                        <Skeleton className="h-8 w-full" />
                      </TableCell>
                    </TableRow>
                  ))
                : pageRows.map((r) => (
                    <TableRow key={r.id} className="cursor-pointer" onClick={() => setSelected(r)}>
                      <TableCell className="ps-4 sm:ps-6">
                        <div className="flex min-w-0 items-center gap-3">
                          <Thumb src={r.thumbnailPath} alt={r.name} size="sm" />
                          <div className="min-w-0">
                            <p className="truncate text-sm">{r.name}</p>
                            {r.sku && <p className="truncate text-xs text-muted-foreground">{r.sku}</p>}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-end tabular-nums">{formatNumber(r.tries)}</TableCell>
                      <TableCell className="text-end tabular-nums">{formatNumber(r.downloads)}</TableCell>
                      <TableCell className="text-end tabular-nums">{pct(r.saveRate)}</TableCell>
                      <TableCell className="text-end tabular-nums">{formatNumber(r.leads)}</TableCell>
                      <TableCell className="text-end">
                        {r.prevTries == null ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <SignedDelta value={r.tries - r.prevTries} />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              {!loading && pageRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length + 1} className="py-10 text-center text-sm text-muted-foreground">
                    {t('VIZ_ANALYTICS.PRODUCTS.NO_MATCH')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        {!loading && filtered.length > pageSize && (
          <TablePagination
            page={page}
            pageSize={pageSize}
            total={filtered.length}
            onPageChange={setPage}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setPage(0);
            }}
            className="border-t"
          />
        )}
      </SectionCard>

      <ProductSheet row={selected} onClose={() => setSelected(null)} />
    </div>
  );
};

/** Tries gained or lost against the previous period: +12 in green, −4 in red. */
function SignedDelta({ value }: { value: number }) {
  if (!value) return <span className="text-xs tabular-nums text-muted-foreground">0</span>;
  return (
    <span className={cn('text-xs font-medium tabular-nums', value > 0 ? 'text-success' : 'text-destructive')}>
      {value > 0 ? '+' : '−'}
      {formatNumber(Math.abs(value))}
    </span>
  );
}

function ListSkeleton() {
  return (
    <div className="flex flex-col gap-2">
      {[0, 1, 2, 3].map((i) => (
        <Skeleton key={i} className="h-9 w-full" />
      ))}
    </div>
  );
}

/** One product's path through the visualizer, and the way to its editor. */
function ProductSheet({ row, onClose }: { row: ProductRow | null; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <Sheet open={!!row} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="gap-0 p-0 data-[side=right]:w-full data-[side=right]:sm:max-w-md">
        {row && (
          <>
            <SheetHeader className="flex-row items-center gap-3 border-b p-5 pe-12">
              <Thumb src={row.thumbnailPath} alt={row.name} size="lg" />
              <div className="min-w-0">
                <SheetTitle className="truncate">{row.name}</SheetTitle>
                <SheetDescription>{row.sku || t('VIZ_ANALYTICS.PRODUCTS.SHEET_SUBTITLE')}</SheetDescription>
              </div>
            </SheetHeader>
            <div className="flex flex-1 flex-col gap-6 overflow-y-auto p-5">
              <div className="grid grid-cols-3 gap-4">
                <MiniStat label={t('VIZ_ANALYTICS.PRODUCTS.COL.TRIES')} value={formatNumber(row.tries)} sub={row.prevTries != null ? t('VIZ_ANALYTICS.PRODUCTS.PREVIOUSLY', { count: row.prevTries }) : undefined} />
                <MiniStat label={t('VIZ_ANALYTICS.PRODUCTS.COL.SAVE_RATE')} value={pct(row.saveRate)} />
                <MiniStat label={t('VIZ_ANALYTICS.PRODUCTS.COL.LEADS')} value={formatNumber(row.leads)} />
              </div>
              <div>
                <p className="mb-3 text-sm font-medium">{t('VIZ_ANALYTICS.PRODUCTS.PATH')}</p>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
                  {[
                    { key: 'shown', label: t('VIZ_ANALYTICS.PRODUCTS.SHOWN'), value: row.impressions },
                    { key: 'tried', label: t('VIZ_ANALYTICS.PRODUCTS.COL.TRIES'), value: row.tries },
                    { key: 'saved', label: t('VIZ_ANALYTICS.PRODUCTS.COL.SAVED'), value: row.downloads },
                    { key: 'shop', label: t('VIZ_ANALYTICS.PRODUCTS.SHOP_CLICKS'), value: row.shopClicks },
                  ].map((s) => (
                    <div key={s.key} className="flex items-baseline justify-between gap-3 border-b pb-2">
                      <dt className="text-xs text-muted-foreground">{s.label}</dt>
                      <dd className="text-sm font-medium tabular-nums">{formatNumber(s.value)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
              <p className="text-xs text-muted-foreground">{t('VIZ_ANALYTICS.PRODUCTS.VISITORS', { count: row.visitors })}</p>
              <Button asChild variant="outline" className="self-start">
                <Link to={VIZ_LINKS.editProduct(row.id)}>
                  <ExternalLink />
                  {t('VIZ_ANALYTICS.PRODUCTS.EDIT')}
                </Link>
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default ProductPerformancePage;
